/* eslint-disable no-else-return */
/**
 * JSON字符串验证工具函数
 * @description 提供多种方式判断字符串是否为有效的JSON格式
 * @author: AI Assistant
 * @version: v1.0.0
 * @Date: 2025-01-02
 */

/**
 * 方法1: 使用try-catch进行JSON解析验证
 * @param str 待验证的字符串
 * @returns 是否为有效的JSON字符串
 */
export const isValidJSON = (str: string): boolean => {
  if (typeof str !== 'string') {
    return false;
  }

  try {
    JSON.parse(str);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * 方法2: 安全解析JSON字符串，返回解析结果或默认值
 * @param str 待解析的字符串
 * @param defaultValue 解析失败时的默认值
 * @returns 解析后的对象或默认值
 */
export const safeParseJSON = <T = any>(str: string, defaultValue: T = {} as T): T => {
  if (typeof str !== 'string' || str.trim() === '') {
    return defaultValue;
  }

  try {
    return JSON.parse(str);
  } catch (error) {
    console.warn('JSON解析失败:', error, '原始字符串:', str);
    return defaultValue;
  }
};

/**
 * 方法3: 使用正则表达式进行基础JSON格式验证
 * @param str 待验证的字符串
 * @returns 是否为可能的JSON格式
 */
export const isPossibleJSON = (str: string): boolean => {
  if (typeof str !== 'string') {
    return false;
  }

  const trimmed = str.trim();

  // 检查是否以 { 或 [ 开头，以 } 或 ] 结尾
  const startsWithBrace = trimmed.startsWith('{') && trimmed.endsWith('}');
  const startsWithBracket = trimmed.startsWith('[') && trimmed.endsWith(']');

  return startsWithBrace || startsWithBracket;
};

/**
 * 方法4: 更严格的JSON格式验证（结合正则和解析）
 * @param str 待验证的字符串
 * @returns 是否为有效的JSON字符串
 */
export const isStrictJSON = (str: string): boolean => {
  if (typeof str !== 'string') {
    return false;
  }

  const trimmed = str.trim();

  // 首先检查基础格式
  if (!isPossibleJSON(trimmed)) {
    return false;
  }

  // 然后尝试解析
  try {
    JSON.parse(trimmed);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * 方法5: 带详细错误信息的JSON解析
 * @param str 待解析的字符串
 * @param defaultValue 解析失败时的默认值
 * @returns 包含解析结果和错误信息的对象
 */
export const parseJSONWithError = <T = any>(
  str: string,
  defaultValue: T = {} as T,
): { success: boolean; data: T; error?: string } => {
  if (typeof str !== 'string' || str.trim() === '') {
    return {
      success: false,
      data: defaultValue,
      error: '输入不是有效的字符串或为空',
    };
  }

  try {
    const parsed = JSON.parse(str);
    return {
      success: true,
      data: parsed,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : '未知错误';
    return {
      success: false,
      data: defaultValue,
      error: `JSON解析失败: ${errorMessage}`,
    };
  }
};

/**
 * 方法6: 判断字符串类型（JSON对象、JSON数组、普通字符串）
 * @param str 待判断的字符串
 * @returns 字符串类型
 */
export const getStringType = (
  str: string,
): 'json-object' | 'json-array' | 'json-primitive' | 'plain-text' => {
  if (typeof str !== 'string') {
    return 'plain-text';
  }

  const trimmed = str.trim();

  if (trimmed === '') {
    return 'plain-text';
  }

  try {
    const parsed = JSON.parse(trimmed);

    if (Array.isArray(parsed)) {
      return 'json-array';
    } else if (typeof parsed === 'object' && parsed !== null) {
      return 'json-object';
    } else {
      return 'json-primitive';
    }
  } catch (error) {
    return 'plain-text';
  }
};
